import { useId, useRef, useState } from 'react';
import {
  ArdoqId,
  APIComponentType,
  APIWorkspaceAttributes,
} from '@ardoq/api-types';
import { FlexBox, Stack } from '@ardoq/layout';
import { Tag, StatusType } from '@ardoq/status-ui';
import { Header2, Text } from '@ardoq/typography';
import { IconName } from '@ardoq/icons';
import { IconButton } from '@ardoq/button';
import { ControlledPopover } from '@ardoq/popovers';
import {
  LargeImagePreviewModal,
  useMarkdownImagePreview,
  ZoomableMarkdownViewer,
  type MarkdownImagePreviewViewerProps,
} from '@ardoq/image-preview';
import { type I18nLocale, t, type WithI18nLocale } from '@ardoq/i18n';
import { WorkspaceManagerLinks } from './WorkspaceManagerLinks';
import { ComponentTypeEntityIcon } from './ComponentTypeEntityIcon';
import type { GetWorkspaceManagerLinkProps } from './types';
import { getImagePreviewLabels } from './imagePreviewLabels';

export type ComponentTypeGroup = {
  componentTypes: APIComponentType[];
  workspaces: APIWorkspaceAttributes[];
};

export type MultipleComponentTypesDatasourceInformationProps = {
  componentTypeGroups: ComponentTypeGroup[];
  canManageWorkspace: (workspaceId: ArdoqId) => boolean;
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps;
  i18nLocale: I18nLocale;
};

type ComponentTypeChipProps = {
  componentTypeGroup: ComponentTypeGroup;
  canManageWorkspace: (workspaceId: ArdoqId) => boolean;
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps;
  i18nLocale: I18nLocale;
};

const ComponentTypeTag = ({ i18nLocale }: WithI18nLocale) => (
  <Tag
    statusType={StatusType.DEFAULT}
    label={t(
      i18nLocale,
      'inventory:datasourceInformation.componentTypeTag',
      'Component type'
    )}
  />
);

const ComponentTypeInfoPopoverContent = ({
  componentTypeGroup,
  canManageWorkspace,
  getWorkspaceManagerLinkProps,
  i18nLocale,
  viewerProps,
}: ComponentTypeChipProps & {
  viewerProps: MarkdownImagePreviewViewerProps;
}) => {
  const { componentTypes, workspaces } = componentTypeGroup;
  const componentType = componentTypes[0];
  const description = componentTypes.find(({ description }) =>
    description?.trim()
  )?.description;

  return (
    <Stack gap="small">
      <FlexBox gap="small" align="center">
        <ComponentTypeEntityIcon componentType={componentType} />
        <Header2>{componentType.name}</Header2>
        <ComponentTypeTag i18nLocale={i18nLocale} />
      </FlexBox>
      {description && (
        <ZoomableMarkdownViewer
          content={description}
          sanitationLevel="permissive"
          style={{ maxHeight: '250px', overflowY: 'auto' }}
          {...viewerProps}
        />
      )}
      <WorkspaceManagerLinks
        workspaces={workspaces}
        canManageWorkspace={canManageWorkspace}
        getWorkspaceManagerLinkProps={getWorkspaceManagerLinkProps}
        i18nLocale={i18nLocale}
      />
    </Stack>
  );
};

const ComponentTypeChip = ({
  componentTypeGroup,
  canManageWorkspace,
  getWorkspaceManagerLinkProps,
  i18nLocale,
}: ComponentTypeChipProps) => {
  const [isInfoPopoverOpen, setIsInfoPopoverOpen] = useState(false);
  const infoPopoverId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { closeButtonText, zoomButtonLabel } =
    getImagePreviewLabels(i18nLocale);
  const { viewerProps, previewImageUrl, closePreview } =
    useMarkdownImagePreview(zoomButtonLabel);

  const componentType = componentTypeGroup.componentTypes[0];

  return (
    <FlexBox gap="xsmall" align="center">
      <ComponentTypeEntityIcon componentType={componentType} />
      <Text variant="text1Bold">{componentType.name}</Text>
      <ControlledPopover
        id={infoPopoverId}
        isOpen={isInfoPopoverOpen}
        onClose={() => {
          setIsInfoPopoverOpen(false);
          triggerRef.current?.focus();
        }}
        onClickOutside={() => setIsInfoPopoverOpen(false)}
        content={
          <ComponentTypeInfoPopoverContent
            componentTypeGroup={componentTypeGroup}
            canManageWorkspace={canManageWorkspace}
            getWorkspaceManagerLinkProps={getWorkspaceManagerLinkProps}
            i18nLocale={i18nLocale}
            viewerProps={viewerProps}
          />
        }
      >
        <IconButton
          ref={triggerRef}
          iconName={IconName.INFO}
          isActive={isInfoPopoverOpen}
          onClick={() => setIsInfoPopoverOpen(prev => !prev)}
          label={t(
            i18nLocale,
            'inventory:datasourceInformation.showComponentTypeInfo',
            { name: componentType.name },
            'Show info for {{name}}'
          )}
        />
      </ControlledPopover>
      {/* Outside the popover: the modal portals to the body, so a click in it
          reads as a click outside the popover and would unmount the modal. */}
      <LargeImagePreviewModal
        imageUrl={previewImageUrl}
        onClose={closePreview}
        closeButtonText={closeButtonText}
      />
    </FlexBox>
  );
};

export const MultipleComponentTypesDatasourceInformation = ({
  componentTypeGroups,
  canManageWorkspace,
  getWorkspaceManagerLinkProps,
  i18nLocale,
}: MultipleComponentTypesDatasourceInformationProps) => (
  <FlexBox
    gap="medium"
    align="center"
    flexWrap
    overflowY="auto"
    padding="xsmall"
    style={{ maxHeight: '200px' }}
  >
    {componentTypeGroups.map(componentTypeGroup => (
      <ComponentTypeChip
        key={componentTypeGroup.componentTypes[0].id}
        componentTypeGroup={componentTypeGroup}
        canManageWorkspace={canManageWorkspace}
        getWorkspaceManagerLinkProps={getWorkspaceManagerLinkProps}
        i18nLocale={i18nLocale}
      />
    ))}
  </FlexBox>
);
